import React from "react";

import Reducer from "./reducer";
import { useMakePayment } from "../../dataloader";
import { isRegPortal } from "shared/utils/config";
import { useCopies } from "../../hooks/useCopies";
import { errorToast } from "../../utils/toastNotification";
import { ApiPaymentRequest } from "../../types/ApiPaymentRequest";
import { TransactionStatus } from "../../enums/TransactionStatus";
import { useReducerWrapper } from "shared/hooks/useReducerWrapper";
import {
  PaymentComponentWrapperArgsType,
  PaymentWrapperDispatchInterface,
  PaymentWrapperService,
  PaymentWrapperState
} from "./type";
import { PaymentWrapperView } from "./enum";
import { ApiPaymentInitTokenResponse } from "../payment/types";

export default function usePaymentWrapperService({
  successHandler,
  errorHandler,
  ...props
}: PaymentComponentWrapperArgsType): PaymentWrapperService {
  const copies = useCopies();
  const { mutateAsync: makePayment } = useMakePayment({ hideLoader: false });
  const [uniqueRandomKey, setUniqueRandomKey] = React.useState(Math.random());
  const [paymentMethodsEnabled, setPaymentMethodsEnabled] = React.useState({
    offlinePaymentEnabled: false,
    invoicePaymentEnabled: false
  });
  const [state, dispatch] = useReducerWrapper<PaymentWrapperState, PaymentWrapperDispatchInterface>(Reducer);

  React.useEffect(() => {
    if (props.invoicePaymentConfig?.initialEmail) dispatch.setShowUpdateInvoicePaymentRequestAction(true);
    if (props.offlinePaymentConfig?.offlinePaymentRequested) {
      dispatch.setShowPaymentModes(false);
      dispatch.setPaymentWrapperView(PaymentWrapperView.OFFLINE_PAYMENT_REQUESTED);
    } else dispatch.setPaymentWrapperView(PaymentWrapperView.PAY_BY_SELF);
  }, [dispatch, props.invoicePaymentConfig?.initialEmail, props.offlinePaymentConfig?.offlinePaymentRequested]);

  const toggleInvoicePayment = React.useCallback(() => {
    dispatch.setPaymentWrapperView(
      state.paymentWrapperView === PaymentWrapperView.REQUEST_PAYMENT
        ? PaymentWrapperView.PAY_BY_SELF
        : PaymentWrapperView.REQUEST_PAYMENT
    );
    dispatch.setShowUpdateInvoicePaymentRequestAction(!state.canShowUpdateInvoicePaymentRequestAction);
  }, [state, dispatch]);

  const toggleOfflinePayment = React.useCallback(() => {
    dispatch.setPaymentWrapperView(
      state.paymentWrapperView === PaymentWrapperView.OFFLINE_PAYMENT
        ? PaymentWrapperView.PAY_BY_SELF
        : PaymentWrapperView.OFFLINE_PAYMENT
    );
    if (props.invoicePaymentConfig?.initialEmail) dispatch.setShowUpdateInvoicePaymentRequestAction(true);
  }, [state, dispatch, props.invoicePaymentConfig?.initialEmail]);

  const setRandomUniqueKeyOnFailure = React.useCallback(() => {
    setUniqueRandomKey(Math.random());
  }, []);

  const setPaymentNonce = React.useCallback(
    async (nonce: string) => {
      const reqObj: ApiPaymentRequest = {
        ...(props.isInvoicePayer && props.invoicePayerId && isRegPortal
          ? { invoicePayerId: props.invoicePayerId }
          : { registrationParticipantId: props.registrationParticipantId }),
        token: nonce,
        purchaseOrderId: props.purchaseOrderId,
        amount: props.amount
      };
      try {
        const data = await makePayment(reqObj);
        if (data.transaction.status === TransactionStatus.FAILED) {
          errorToast(copies.get("payment.transaction.failed"));
          errorHandler?.(data);
          setRandomUniqueKeyOnFailure();
        } else {
          successHandler?.(data);
        }
      } catch (e) {
        errorHandler?.(e);
        setRandomUniqueKeyOnFailure();
      }
    },
    [
      makePayment,
      errorHandler,
      successHandler,
      props.amount,
      props.purchaseOrderId,
      props.invoicePayerId,
      props.registrationParticipantId,
      setRandomUniqueKeyOnFailure,
      copies,
      props.isInvoicePayer
    ]
  );

  const tokenInitialisedHandler = React.useCallback(
    (data: ApiPaymentInitTokenResponse) =>
      setPaymentMethodsEnabled({
        offlinePaymentEnabled: data.offlinePaymentEnabled,
        invoicePaymentEnabled: Boolean(props.invoicePaymentEnabled)
      }),
    [props.invoicePaymentEnabled]
  );

  const backToOfflineRequestedClickHandler = React.useCallback(() => {
    dispatch.setShowPaymentModes(false);
    dispatch.setPaymentWrapperView(PaymentWrapperView.OFFLINE_PAYMENT_REQUESTED);
  }, [dispatch]);

  return {
    state,
    dispatch,
    setPaymentNonce,
    uniqueRandomKey,
    toggleInvoicePayment,
    toggleOfflinePayment,
    paymentMethodsEnabled,
    tokenInitialisedHandler,
    backToOfflineRequestedClickHandler
  };
}
